import { Tag } from "antd";
import { ReactNode } from "react";
import { InputSkeletonLoading } from "../Skeleton/CustomSkeleton";
import ReportAssetCard from "./ReportAssetCard";
import { ReportHeaderFact } from "./ReportHeader";
import { TenantLogo } from "./TenantLogo";

// The fields of a report, once its lookup has named them, that these read.
type LookedUpReport = {
	is_project_related?: boolean;
	project_name?: string;
	project_area?: string;
	well?: string;
	well_name?: string;
	operation_name?: string;
	phase_name?: string;
	rig?: string;
	rig_image?: string;
	reviewer_name?: string;
	operator?: string;
};

// A fact the page looks up, such as a department's name: a placeholder while
// the lookup loads, then the value, or a dash when there is none.
export function LookedUpValue({
	value,
	isLoading,
}: Readonly<{ value?: ReactNode; isLoading: boolean }>) {
	return (
		<InputSkeletonLoading loading={isLoading}>
			<span>{value ?? "—"}</span>
		</InputSkeletonLoading>
	);
}

// Where a project-related report belongs: its project, asset, operation and
// phase, by the names its lookup found. Other reports have none.
export function getProjectFacts(
	report: LookedUpReport | null | undefined,
	isLoading: boolean
): ReportHeaderFact[] {
	if (!report?.is_project_related) return [];

	const asset = (
		<>
			{report.project_area ? <Tag>{report.project_area}</Tag> : null}
			{report.well_name ?? "—"}
		</>
	);
	return [
		{
			key: "project",
			label: "Project",
			value: (
				<LookedUpValue value={report.project_name} isLoading={isLoading} />
			),
		},
		{
			key: "asset",
			label: "Asset",
			value: <LookedUpValue value={asset} isLoading={isLoading} />,
		},
		{
			key: "operation",
			label: "Operation",
			value: (
				<LookedUpValue value={report.operation_name} isLoading={isLoading} />
			),
		},
		{
			key: "phase",
			label: "Phase",
			value: <LookedUpValue value={report.phase_name} isLoading={isLoading} />,
		},
	];
}

// Who reviews the report, by the name its lookup found.
export const getReviewerFact = (
	report: LookedUpReport | null | undefined,
	isLoading: boolean
): ReportHeaderFact => ({
	key: "reviewer",
	label: "Reviewer",
	value: <LookedUpValue value={report?.reviewer_name} isLoading={isLoading} />,
});

// The tenant the report belongs to, by its logo and name.
export const getTenantFact = (operator?: string): ReportHeaderFact => ({
	key: "tenant",
	label: "Tenant",
	value: <TenantLogo tenantKey={operator ?? ""} size={24} showName />,
});

// The rig and well of a project-related report, by the names its lookup
// found, for the header's media. Other reports have none.
export function getAssetCard(
	report: LookedUpReport | null | undefined,
	isLoading: boolean
): JSX.Element | undefined {
	if (!report?.is_project_related) return undefined;
	return (
		<ReportAssetCard
			rigName={report.rig}
			rigImage={report.rig_image}
			wellName={report.well_name}
			wellId={report.well}
			operatorKey={report.operator}
			isLoading={isLoading}
		/>
	);
}
